/**
 * CPU-rendered canvas-section snapshots for inactive previews.
 *
 * Inactive canvas sections used to mount a full CanvasKit/WebGL editor each,
 * which capped emails at ~5 canvas sections before browsers started evicting
 * GL contexts. This module renders the same scene via Skia's CPU-only
 * `MakeSurface` (no WebGL) once per (resolved-doc) hash, caches the resulting
 * PNG data URL in memory, and the consumer renders it as an `<img>`.
 *
 * Result: 0 WebGL contexts for any number of inactive sections; only the
 * actively-edited section uses real CanvasKit/WebGL.
 */

import { useEffect, useMemo, useState } from 'react';
import type { CanvasKit } from 'canvaskit-wasm';
import type { FontMgr } from 'canvaskit-wasm/full';

import type { Paint, SceneDocument } from '@/lib/scene-document';
import { normalizeSceneDocument } from '@/lib/scene-document';
import { getCanvasKit, getFontMgr } from '@/lib/canvaskit-init';
import {
  applyFillToPaint,
  getCachedImage,
  renderNode,
  type RenderContext,
} from '@/components/canvas-editor/canvaskit-render-scene';
import {
  resolveMergeTagsInTree,
  type BrandResolverContext,
  type SlotResolver,
} from '@nexmail/shared-util/email-builder';

// ─── Cache (LRU, in-memory) ──────────────────────────────────────────────────

const CACHE_LIMIT = 200;
const cache = new Map<string, string>();

function cacheGet(key: string): string | undefined {
  const v = cache.get(key);
  if (v !== undefined) {
    // Touch: move to most-recently-used
    cache.delete(key);
    cache.set(key, v);
  }
  return v;
}

function cachePut(key: string, value: string): void {
  if (cache.has(key)) cache.delete(key);
  cache.set(key, value);
  while (cache.size > CACHE_LIMIT) {
    const oldest = cache.keys().next().value;
    if (oldest === undefined) break;
    cache.delete(oldest);
  }
}

// ─── Helpers ─────────────────────────────────────────────────────────────────

function hashString(s: string): string {
  let h = 5381;
  for (let i = 0; i < s.length; i++) {
    h = ((h * 33) ^ s.charCodeAt(i)) >>> 0;
  }
  return h.toString(36);
}

function bytesToDataUrl(bytes: Uint8Array, mimeType = 'image/png'): string {
  let binary = '';
  for (let i = 0; i < bytes.length; i++) {
    binary += String.fromCharCode(bytes[i]);
  }
  return `data:${mimeType};base64,${btoa(binary)}`;
}

// ─── Render ──────────────────────────────────────────────────────────────────

function renderSceneToDataUrl(
  ck: CanvasKit,
  fontMgr: FontMgr,
  doc: SceneDocument,
  scale: number,
  onImageLoaded: () => void,
): string | null {
  const w = Math.ceil(doc.width * scale);
  const h = Math.ceil(doc.height * scale);
  if (w <= 0 || h <= 0) return null;

  // CPU-only surface — no WebGL context allocated.
  const offscreen = ck.MakeSurface(w, h);
  if (!offscreen) return null;

  const skCanvas = offscreen.getCanvas();
  skCanvas.clear(ck.Color(255, 255, 255, 1));
  skCanvas.scale(scale, scale);

  // Document-level fills (mirrors CanvasKitEditor.toDataURL)
  if (doc.fills?.length) {
    const bgRect = ck.LTRBRect(0, 0, doc.width, doc.height);
    for (const fill of doc.fills as Paint[]) {
      if (fill.visible === false) continue;
      if (fill.type === 'IMAGE') {
        if (!fill.src) continue;
        const cached = getCachedImage(fill.src, Math.max(doc.width, doc.height));
        if (!cached) {
          // Image not cached — kick off a load so the retry can pick it up.
          onImageLoaded();
          continue;
        }
        const imgW = cached.width();
        const imgH = cached.height();
        const s = Math.max(doc.width / imgW, doc.height / imgH);
        const cropW = doc.width / s;
        const cropH = doc.height / s;
        const cropX = (imgW - cropW) / 2;
        const cropY = (imgH - cropH) / 2;
        const imgPaint = new ck.Paint();
        if (fill.opacity != null && fill.opacity < 1) imgPaint.setAlphaf(fill.opacity);
        skCanvas.drawImageRect(
          cached,
          ck.LTRBRect(cropX, cropY, cropX + cropW, cropY + cropH),
          bgRect,
          imgPaint,
        );
        imgPaint.delete();
      } else {
        const fp = new ck.Paint();
        fp.setStyle(ck.PaintStyle.Fill);
        applyFillToPaint(ck, fp, fill, doc.width, doc.height);
        skCanvas.drawRect(bgRect, fp);
        fp.delete();
      }
    }
  }

  const ctx: RenderContext = {
    ck,
    canvas: skCanvas,
    doc,
    fontMgr,
    onImageLoaded,
    zoom: scale,
  };

  for (const rootId of doc.rootChildren) {
    const node = doc.nodes[rootId];
    if (node) renderNode(ctx, node);
  }

  offscreen.flush();
  const img = offscreen.makeImageSnapshot();
  offscreen.delete();
  if (!img) return null;

  const bytes = img.encodeToBytes();
  img.delete();
  if (!bytes) return null;

  return bytesToDataUrl(bytes);
}

// ─── Hook ────────────────────────────────────────────────────────────────────

export interface CanvasSnapshotInput {
  canvasJson: Record<string, unknown> | null | undefined;
  width: number;
  height: number;
  brand: BrandResolverContext | null | undefined;
  slotResolver: SlotResolver | undefined;
}

export interface CanvasSnapshotResult {
  dataUrl: string | null;
  ready: boolean;
}

/**
 * Returns a PNG data URL for a canvas section's resolved scene document.
 * Cached by content hash; first render is async (CanvasKit init), subsequent
 * mounts of the same content are synchronous cache hits. Re-renders when
 * scene images load asynchronously.
 */
export function useCanvasSnapshot(input: CanvasSnapshotInput): CanvasSnapshotResult {
  const { resolvedDoc, key } = useMemo(() => {
    const raw =
      (input.canvasJson as SceneDocument | null | undefined) ??
      ({
        version: 2,
        width: input.width,
        height: input.height,
        nodes: {},
        rootChildren: [],
      } as unknown as SceneDocument);
    const cloned = structuredClone(raw);
    resolveMergeTagsInTree(cloned, input.brand ?? undefined, input.slotResolver);
    const normalized = normalizeSceneDocument(cloned);
    return { resolvedDoc: normalized, key: hashString(JSON.stringify(normalized)) };
  }, [input.canvasJson, input.width, input.height, input.brand, input.slotResolver]);

  const initialCached = cacheGet(key);
  const [dataUrl, setDataUrl] = useState<string | null>(initialCached ?? null);
  const [ready, setReady] = useState<boolean>(initialCached !== undefined);

  useEffect(() => {
    let cancelled = false;
    let retryTimer: ReturnType<typeof setTimeout> | null = null;

    const cached = cacheGet(key);
    if (cached) {
      setDataUrl(cached);
      setReady(true);
      return;
    }
    setReady(false);

    let ckRef: CanvasKit | null = null;
    let fontMgrRef: FontMgr | null = null;

    const scheduleRetry = () => {
      if (cancelled || retryTimer != null) return;
      retryTimer = setTimeout(() => {
        retryTimer = null;
        if (cancelled || !ckRef || !fontMgrRef) return;
        const result = renderSceneToDataUrl(ckRef, fontMgrRef, resolvedDoc, 2, scheduleRetry);
        if (cancelled || !result) return;
        cachePut(key, result);
        setDataUrl(result);
        setReady(true);
      }, 250);
    };

    void (async () => {
      const [ck, fontMgr] = await Promise.all([getCanvasKit(), getFontMgr()]);
      if (cancelled) return;
      ckRef = ck;
      fontMgrRef = fontMgr;

      const result = renderSceneToDataUrl(ck, fontMgr, resolvedDoc, 2, scheduleRetry);
      if (cancelled || !result) return;
      cachePut(key, result);
      setDataUrl(result);
      setReady(true);
    })();

    return () => {
      cancelled = true;
      if (retryTimer != null) {
        clearTimeout(retryTimer);
        retryTimer = null;
      }
    };
  }, [key, resolvedDoc]);

  return { dataUrl, ready };
}
